import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Headset, User } from "lucide-react";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAdminRoster, adminDisplayName } from "@/hooks/useAdminRoster";
import { TenantJourneyStageBadge } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
import { KIND_META } from "@/lib/brain/kind-meta";
import { withBrainReturn } from "@/lib/brain/deck";
import {
  formatBrainDate,
  formatBrainEur,
  formatBrainMonth,
} from "@/lib/brain/time";
import {
  BRAIN_PRIORITIES,
  type BrainItemNote,
  type BrainPriority,
  type DeckCard,
} from "@/lib/brain/types";
import { BrainCardActions, useTicketAssign } from "./BrainCardActions";
import { FilledActionScope } from "./FilledAction";

const PRIORITY_STYLE: Record<BrainPriority, string> = {
  urgent: "bg-rose-50 text-rose-700 border-rose-200",
  high: "bg-amber-50 text-amber-800 border-amber-200",
  normal: "bg-slate-50 text-slate-700 border-slate-200",
  low: "bg-slate-50 text-slate-500 border-slate-200",
};

export interface BrainCardProps {
  card: DeckCard;
  position: number;
  total: number;
  me: string;
  onPrev(): void;
  onNext(): void;
  onAcknowledge(note: string | null): void;
  onAssign(assigneeId: string | null, note: string | null): void;
  /** Shared notes on this card, oldest first. */
  notes?: BrainItemNote[];
  onAddNote(note: string): void;
  onPriority(level: BrainPriority | null): void;
  onDone(): void;
}

export function BrainCard(props: BrainCardProps) {
  const { card, position, total, me } = props;
  const { t, language } = useLanguage();
  const { roster } = useAdminRoster();
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  // An assign the user picked from the dropdown, held open so they can type the
  // note that rides along to the assignee. null = the box is in team-note mode.
  const [pendingAssign, setPendingAssign] = useState<{
    id: string | null;
  } | null>(null);
  const meta = KIND_META[card.kind];
  // A ticket carries its own assignee (issues.assigned_to), which the deck reads
  // instead of brain_item_state — so assigning one writes the ticket.
  const sourceAssigned = card.sourceAssigneeId !== undefined;
  const ticketAssign = useTicketAssign(card.refs.ticketId, props.onDone);
  const assigneeLabel =
    card.assigneeId === null
      ? sourceAssigned
        ? t("brain.assign.unassigned", "Unassigned")
        : t("brain.scope.everyone", "Unassigned")
      : card.assigneeId === me
        ? t("brain.scope.mine", "Mine")
        : adminDisplayName(
            roster.find((a) => a.id === card.assigneeId),
            t
          );
  // A support card's `who` is the ticket's title, not a person.
  const WhoIcon = card.category === "support" ? Headset : User;
  // One filled button per card: Open, unless the card's own action shows one.
  const [filled, setFilled] = useState(0);
  const noteOrNull = () => note.trim() || null;
  // Closing the box cancels the draft: hidden text must never ride along on
  // the next Acknowledge or Assign, and a cancelled assign never fires.
  const closeNote = () => {
    setNote("");
    setNoteOpen(false);
    setPendingAssign(null);
  };
  // Picking an assignee opens the box first; the assign fires on confirm with
  // the note the assignee will see. Deferring it keeps the card from reordering
  // out from under the box before the note is typed (COL-593 item 8).
  const assign = (assigneeId: string | null) => {
    setNote("");
    setPendingAssign({ id: assigneeId });
    setNoteOpen(true);
  };
  const confirmAssign = () => {
    if (!pendingAssign) return;
    const n = noteOrNull();
    if (sourceAssigned)
      void ticketAssign.assignTo(pendingAssign.id, n ?? undefined);
    else props.onAssign(pendingAssign.id, n);
    closeNote();
  };
  const addNote = () => {
    const text = noteOrNull();
    if (!text) return;
    props.onAddNote(text);
    setNote("");
  };
  // Two staff accounts can share a name: tell them apart by email.
  const nameOf = (a: (typeof roster)[number]) => {
    const name = adminDisplayName(a, t);
    const twins = roster.filter((b) => adminDisplayName(b, t) === name);
    return twins.length > 1 && a.email ? `${name} (${a.email})` : name;
  };
  const authorOf = (id: string | null) => {
    const a = roster.find((r) => r.id === id);
    return a ? nameOf(a) : adminDisplayName(null, t);
  };

  return (
    <Card className="w-full p-5" data-testid="brain-card" data-key={card.key}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Select
          value={card.priority}
          onValueChange={(v) =>
            props.onPriority(v === "auto" ? null : (v as BrainPriority))
          }
        >
          <SelectTrigger
            className={`h-6 w-auto gap-1 rounded-full border px-2 text-xs ${PRIORITY_STYLE[card.priority]}`}
            title={t(card.priorityReason.key, card.priorityReason.fallback)}
            aria-label={t("brain.priority.label", "Priority")}
          >
            <SelectValue />
            {card.manualPriority && (
              <span className="opacity-70">
                · {t("brain.priority.manualShort", "manual")}
              </span>
            )}
          </SelectTrigger>
          <SelectContent>
            {BRAIN_PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                {t(`brain.priority.${p}`, p[0].toUpperCase() + p.slice(1))}
              </SelectItem>
            ))}
            {card.manualPriority && (
              <SelectItem value="auto">
                {t("brain.priority.auto", "Back to automatic")}
              </SelectItem>
            )}
          </SelectContent>
        </Select>
        <Badge variant="outline">
          {t(`brain.category.${card.category}`, card.category)}
        </Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* A real button: Badge can't hold the ref Radix positions the menu by. */}
            <button
              type="button"
              className={badgeVariants({ variant: "secondary" })}
              aria-label={`${t("brain.assign.title", "Assign to")}: ${assigneeLabel}`}
            >
              {assigneeLabel}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>
              {t("brain.assign.title", "Assign to")}
            </DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => assign(null)}>
              {t("brain.scope.everyone", "Unassigned")}
            </DropdownMenuItem>
            {roster.map((a) => (
              <DropdownMenuItem key={a.id} onSelect={() => assign(a.id)}>
                {nameOf(a)}
                {a.id === me ? ` (${t("brain.assign.you", "you")})` : ""}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {card.stageId && <TenantJourneyStageBadge stageId={card.stageId} />}
        <span className="ml-auto text-muted-foreground">
          {card.ageDays === 0
            ? t("brain.age.today", "today")
            : card.ageDays === 1
              ? t("brain.age.day", "open 1 day")
              : t("brain.age.days", "open {n} days").replace(
                  "{n}",
                  String(card.ageDays)
                )}
        </span>
      </div>

      <h2 className="mt-3 text-lg font-semibold">
        {t(meta.title.key, meta.title.fallback)}
      </h2>
      {card.who && (
        <Link
          to={withBrainReturn(card.who.href, card.key)}
          className="mt-1 flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          <WhoIcon className="h-3.5 w-3.5" aria-hidden />
          {card.who.label}
        </Link>
      )}
      {card.where && (
        <Link
          to={withBrainReturn(card.where.href, card.key)}
          className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground hover:underline"
        >
          <Building2 className="h-3.5 w-3.5" aria-hidden />
          {card.where.label}
        </Link>
      )}
      {card.summary && <p className="mt-2 text-sm">{card.summary}</p>}
      {card.assignNote && (
        <p className="mt-2 rounded bg-muted px-2 py-1 text-xs">
          {t("brain.assign.noteLabel", "Note")}: {card.assignNote}
        </p>
      )}
      {!!props.notes?.length && (
        <ul
          className="mt-2 space-y-1"
          aria-label={t("brain.notes.title", "Notes")}
        >
          {props.notes.map((n) => (
            <li key={n.id} className="rounded bg-muted px-2 py-1 text-xs">
              <span className="text-muted-foreground">
                {authorOf(n.author_id)} ·{" "}
                {formatBrainDate(n.created_at, language)}:
              </span>{" "}
              {n.note}
            </li>
          ))}
        </ul>
      )}
      {card.facts.length > 0 && (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-3 text-sm">
          {card.facts.map((f) => (
            <div key={f.label.key}>
              <dt className="text-xs uppercase text-muted-foreground">
                {t(f.label.key, f.label.fallback)}
              </dt>
              <dd>
                {f.parts
                  ? f.parts.map((p) => t(p.key, p.fallback)).join(", ")
                  : f.date
                    ? formatBrainDate(f.date, language)
                    : f.month != null || f.eur != null
                      ? [
                          f.month && formatBrainMonth(f.month, language),
                          f.eur != null && formatBrainEur(f.eur, language),
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {!!card.lateRent?.length && (
        <table className="mt-3 w-full border-t pt-3 text-sm">
          <thead className="text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-1 text-left font-normal">
                {t("brain.late.property", "Property")}
              </th>
              <th className="py-1 text-left font-normal">
                {t("brain.late.tenant", "Tenant")}
              </th>
              <th className="py-1 text-left font-normal">
                {t("brain.late.dueSince", "Due since")}
              </th>
              <th className="py-1 text-right font-normal">
                {t("brain.late.open", "Outstanding")}
              </th>
              <th className="py-1 text-right font-normal">
                {t("brain.late.fees", "Late fees")}
              </th>
            </tr>
          </thead>
          <tbody>
            {card.lateRent.map((r) => (
              <tr key={r.bookingId} className="border-t">
                <td className="py-1">{r.property ?? "—"}</td>
                <td className="py-1">
                  <Link
                    to={withBrainReturn(
                      `/admin?tab=tenants&bookingId=${r.bookingId}`,
                      card.key
                    )}
                    className="text-primary hover:underline"
                  >
                    {r.tenant || `#${r.bookingId}`}
                  </Link>
                </td>
                <td className="py-1">
                  {r.dueSince ? formatBrainDate(r.dueSince, language) : "—"}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {formatBrainEur(r.openEur, language)}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {formatBrainEur(r.feesEur, language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <FilledActionScope count={setFilled}>
        <BrainCardActions card={card} onDone={props.onDone} />
      </FilledActionScope>

      {noteOpen && (
        <div className="mt-3 flex gap-2">
          <Input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                closeNote();
                return;
              }
              // Enter commits: fires the held assign when one is pending (with
              // the note the assignee sees), else saves a team note and keeps
              // the card so more can be added before Acknowledge.
              if (e.key === "Enter" && !e.repeat) {
                e.preventDefault();
                if (pendingAssign) confirmAssign();
                else addNote();
              }
            }}
            placeholder={
              pendingAssign
                ? t(
                    "brain.assign.notePlaceholder",
                    "Note for the assignee — Enter to assign"
                  )
                : t(
                    "brain.notes.placeholder",
                    "Note for the team — Enter to save"
                  )
            }
            aria-label={
              pendingAssign
                ? t("brain.assign.title", "Assign to")
                : t("brain.ack.note", "Add note")
            }
          />
          <Button
            variant="outline"
            size="sm"
            className="h-10"
            // The assign can go through without a note; a team note can't.
            disabled={!pendingAssign && !note.trim()}
            onClick={pendingAssign ? confirmAssign : addNote}
          >
            {pendingAssign
              ? t("brain.assign.confirm", "Assign")
              : t("brain.notes.save", "Save note")}
          </Button>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onPrev}
          aria-label={t("brain.nav.prev", "Previous card")}
        >
          ‹
        </Button>
        <span className="text-sm tabular-nums">
          {position + 1} / {total}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onNext}
          aria-label={t("brain.nav.next", "Next card")}
        >
          ›
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          aria-expanded={noteOpen}
          onClick={() =>
            noteOpen ? closeNote() : (setPendingAssign(null), setNoteOpen(true))
          }
        >
          {t("brain.ack.note", "Add note")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          // A typed note is saved for the team first, never dropped.
          onClick={() => {
            addNote();
            props.onAcknowledge(null);
          }}
        >
          {t("brain.ack.button", "Acknowledge")}
        </Button>
        <Button asChild size="sm" variant={filled > 0 ? "outline" : "default"}>
          <Link to={withBrainReturn(card.primaryHref, card.key)}>
            {t("brain.open", "Open")}
          </Link>
        </Button>
      </div>
    </Card>
  );
}
